import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import SecondHome from "./SecondHome";
import ThirdHome from "./ThirdHome";
import FourthHome from "./FourthHome";
import Footer from "./Footer";
import "../assets/scss/module/home.scss";

/* ------------------------------------------------------------------ */
/*  Global keyframes / resets injected once                            */
/* ------------------------------------------------------------------ */
const GLOBAL_CSS = `
@keyframes hm-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.35;transform:scale(.85)} }
@keyframes hm-float { 0%,100%{transform:translate3d(0,0,0)} 50%{transform:translate3d(0,-18px,0)} }
@keyframes hm-rise { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
@keyframes hm-shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
.hm-rise{animation:hm-rise .7s cubic-bezier(.16,1,.3,1) both}
.hm-d1{animation-delay:.05s}.hm-d2{animation-delay:.14s}.hm-d3{animation-delay:.23s}
.hm-d4{animation-delay:.32s}.hm-d5{animation-delay:.41s}
.hm-btn-primary{transition:transform .18s cubic-bezier(.16,1,.3,1),box-shadow .18s ease,filter .18s ease}
.hm-btn-primary:hover{transform:translateY(-2px);filter:brightness(1.06)}
.hm-btn-primary:active{transform:translateY(0)}
.hm-btn-ghost{transition:background-color .18s ease,border-color .18s ease,transform .18s ease}
.hm-btn-ghost:hover{transform:translateY(-2px)}
.hm-card{transition:transform .3s cubic-bezier(.16,1,.3,1),border-color .3s ease}
.hm-card:hover{transform:translateY(-4px)}
.hm-feature{transition:background-color .25s ease,transform .25s ease}
.hm-feature:hover{transform:translateY(-3px)}
.hm-skeleton{background:linear-gradient(90deg,rgba(255,255,255,.06) 25%,rgba(255,255,255,.14) 37%,rgba(255,255,255,.06) 63%);background-size:200% 100%;animation:hm-shimmer 1.4s ease infinite;border-radius:8px}
@media (prefers-reduced-motion: reduce){*{animation:none!important;transition:none!important}}
`;

function useInjectedStyles() {
  useEffect(() => {
    if (document.getElementById("hm-global-styles")) return;
    const el = document.createElement("style");
    el.id = "hm-global-styles";
    el.innerHTML = GLOBAL_CSS;
    document.head.appendChild(el);
  }, []);
}

/* ------------------------------------------------------------------ */
/*  Icons (inline, no dependency)                                      */
/* ------------------------------------------------------------------ */
const IconChart = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" />
  </svg>
);
const IconShield = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /><path d="m9 12 2 2 4-4" />
  </svg>
);
const IconBolt = ({ color }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />
  </svg>
);
const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function Home({ name = "Guest", theme = "green" }) {
  useInjectedStyles();

  const isGreen = theme === "green";
  const primaryColor = isGreen ? "#10b981" : "#3b82f6";
  const primaryGlow = isGreen ? "rgba(16,185,129,0.25)" : "rgba(59,130,246,0.25)";
  const primarySoft = isGreen ? "rgba(16,185,129,0.10)" : "rgba(59,130,246,0.10)";

  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const dataRef = useRef([]);
  const rafRef = useRef(null);

  const [price, setPrice] = useState(null);
  const [priceChange, setPriceChange] = useState(0);
  const [connected, setConnected] = useState(false);
  const [showSecondHome, setShowSecondHome] = useState(true);

  const up = priceChange >= 0;
  const accent = up ? "#10b981" : "#ef4444";

  /* ---------------- title ---------------- */
  useEffect(() => {
    document.title = `${name} — Modern Trading Platform`;
  }, [name]);

  /* ---------------- responsive ---------------- */
  useEffect(() => {
    const onResize = () => setShowSecondHome(window.innerWidth > 1000);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const navigateTo = (path) => {
    if (typeof window !== "undefined") window.location.href = path;
  };

  /* ---------------- chart ---------------- */
  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (!w || !h) return;

    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const points = dataRef.current;
    if (!points || points.length < 2) return;

    const pad = 14;
    const innerW = w - pad * 2;
    const innerH = h - pad * 2;

    let min = Infinity, max = -Infinity;
    for (const p of points) {
      if (p.v < min) min = p.v;
      if (p.v > max) max = p.v;
    }
    if (min === max) { min *= 0.999; max *= 1.001; }

    const stepX = innerW / (points.length - 1);
    const yOf = (v) => pad + innerH - ((v - min) / (max - min)) * innerH;

    // grid
    ctx.strokeStyle = "rgba(255,255,255,0.045)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i <= 4; i++) {
      const y = Math.round(pad + (innerH / 4) * i) + 0.5;
      ctx.moveTo(pad, y);
      ctx.lineTo(pad + innerW, y);
    }
    ctx.stroke();

    // smoothed path
    const path = new Path2D();
    path.moveTo(pad, yOf(points[0].v));
    for (let i = 1; i < points.length; i++) {
      const x0 = pad + (i - 1) * stepX, y0 = yOf(points[i - 1].v);
      const x1 = pad + i * stepX, y1 = yOf(points[i].v);
      const cx = (x0 + x1) / 2;
      path.bezierCurveTo(cx, y0, cx, y1, x1, y1);
    }

    // fill
    const fill = new Path2D(path);
    fill.lineTo(pad + innerW, pad + innerH);
    fill.lineTo(pad, pad + innerH);
    fill.closePath();
    const grad = ctx.createLinearGradient(0, pad, 0, pad + innerH);
    grad.addColorStop(0, primaryGlow);
    grad.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = grad;
    ctx.fill(fill);

    // glow stroke
    ctx.save();
    ctx.shadowColor = primaryGlow;
    ctx.shadowBlur = 14;
    ctx.strokeStyle = primaryColor;
    ctx.lineWidth = 2.4;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.stroke(path);
    ctx.restore();

    // marker
    const lastX = pad + innerW;
    const lastY = yOf(points[points.length - 1].v);
    ctx.beginPath();
    ctx.fillStyle = primarySoft;
    ctx.arc(lastX, lastY, 9, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.fillStyle = "#ffffff";
    ctx.strokeStyle = primaryColor;
    ctx.lineWidth = 2;
    ctx.arc(lastX, lastY, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }, [primaryColor, primaryGlow, primarySoft]);

  const scheduleDraw = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      drawChart();
    });
  }, [drawChart]);

  /* ---------------- live feed ---------------- */
  useEffect(() => {
    let mounted = true;

    fetch("https://api.binance.com/api/v3/klines?symbol=BTCUSDT&interval=1m&limit=60")
      .then((r) => r.json())
      .then((klines) => {
        if (!mounted || !Array.isArray(klines)) return;
        const points = klines.map((k) => ({ t: k[0], v: parseFloat(k[4]) }));
        dataRef.current = points;
        if (points.length) {
          const first = points[0].v;
          const latest = points[points.length - 1].v;
          setPrice(latest);
          setPriceChange(((latest - first) / first) * 100);
        }
        scheduleDraw();
      })
      .catch(() => {});

    let ws;
    try {
      ws = new WebSocket("wss://stream.binance.com:9443/ws/btcusdt@kline_1m");
      wsRef.current = ws;
      ws.onopen = () => mounted && setConnected(true);
      ws.onclose = () => mounted && setConnected(false);
      ws.onerror = () => mounted && setConnected(false);
      ws.onmessage = (ev) => {
        if (!mounted) return;
        try {
          const msg = JSON.parse(ev.data);
          if (!msg?.k) return;
          const close = parseFloat(msg.k.c);
          const time = msg.k.t;
          const points = dataRef.current;
          const last = points[points.length - 1];

          if (last && last.t === time) last.v = close;
          else {
            points.push({ t: time, v: close });
            if (points.length > 120) points.shift();
          }
          if (points.length) {
            const first = points[0].v;
            setPriceChange(((close - first) / first) * 100);
          }
          setPrice(close);
          scheduleDraw();
        } catch (_) {}
      };
    } catch (_) {}

    const onResize = () => scheduleDraw();
    window.addEventListener("resize", onResize);

    return () => {
      mounted = false;
      try { ws && ws.close(); } catch (_) {}
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
    };
  }, [scheduleDraw]);

  const formattedPrice = useMemo(
    () =>
      price
        ? price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : null,
    [price]
  );

  const features = [
    { icon: <IconChart color={primaryColor} />, title: "Deep Analytics", desc: "Real-time streaming and custom technical indicators." },
    { icon: <IconShield color={primaryColor} />, title: "Enterprise Security", desc: "Cold storage isolation and multi-sig authorization." },
  ];

  const stats = [
    { k: "$4.2B", v: "24h volume" },
    { k: "180+", v: "Markets" },
    { k: "99.99%", v: "Uptime" },
    { k: "0.02%", v: "Maker fee" },
  ];

  return (
    <div style={styles.pageWrapper}>
      {/* ambient background */}
      <div style={styles.bgLayer} aria-hidden="true">
        <div style={{ ...styles.orb, ...styles.orbA, background: `radial-gradient(circle, ${primaryGlow} 0%, transparent 70%)` }} />
        <div style={{ ...styles.orb, ...styles.orbB }} />
        <div style={styles.grid} />
      </div>

      <main style={styles.container}>
        <section style={styles.heroGrid}>
          {/* ---------------- left ---------------- */}
          <div style={styles.heroLeft}>
            <div
              className="hm-rise hm-d1"
              style={{ ...styles.badge, borderColor: `${primaryColor}40`, backgroundColor: primarySoft, color: primaryColor }}
            >
              <span style={{ ...styles.badgeDot, backgroundColor: primaryColor, animation: "hm-pulse 1.8s ease-in-out infinite" }} />
              Next-Gen Trading Platform
            </div>

            <h1 className="hm-rise hm-d2" style={styles.heading}>
              {name && name !== "Guest" ? `Hello, ${name} — ` : ""}
              Trade the markets with{" "}
              <span
                style={{
                  background: `linear-gradient(120deg, ${primaryColor}, #ffffff 85%)`,
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                institutional precision
              </span>
            </h1>

            <p className="hm-rise hm-d3" style={styles.subheading}>
              Access global markets with real-time data feeds, sub-millisecond execution, and
              enterprise-grade portfolio analytics in one unified workspace.
            </p>

            <div className="hm-rise hm-d4" style={styles.ctaGroup}>
              <button
                type="button"
                className="hm-btn-primary"
                onClick={() => navigateTo("/exchange")}
                style={{
                  ...styles.primaryBtn,
                  background: `linear-gradient(135deg, ${primaryColor}, ${isGreen ? "#059669" : "#2563eb"})`,
                  boxShadow: `0 12px 32px ${primaryGlow}`,
                }}
              >
                Explore Live Charts
                <IconArrow />
              </button>

              <button
                type="button"
                className="hm-btn-ghost"
                onClick={() => navigateTo("/profile")}
                style={{ ...styles.secondaryBtn, borderColor: `${primaryColor}45`, color: "#e2e8f0" }}
              >
                View Portfolio
              </button>
            </div>

            <div className="hm-rise hm-d5" style={styles.statStrip}>
              {stats.map((s) => (
                <div key={s.v} style={styles.stat}>
                  <div style={styles.statK}>{s.k}</div>
                  <div style={styles.statV}>{s.v}</div>
                </div>
              ))}
            </div>

            <div className="hm-rise hm-d5" style={styles.featureGrid}>
              {features.map((f) => (
                <div key={f.title} className="hm-feature" style={styles.featureCard}>
                  <div style={{ ...styles.iconBox, backgroundColor: primarySoft, border: `1px solid ${primaryColor}25` }}>
                    {f.icon}
                  </div>
                  <div>
                    <h3 style={styles.featureTitle}>{f.title}</h3>
                    <p style={styles.featureDesc}>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ---------------- right ---------------- */}
          <div style={styles.heroRight}>
            <div className="hm-card hm-rise hm-d3" style={styles.cardContainer}>
              <div style={styles.cardHeader}>
                <div>
                  <div style={styles.pairLabel}>BTC / USDT</div>
                  {formattedPrice ? (
                    <div style={styles.priceDisplay}>${formattedPrice}</div>
                  ) : (
                    <div className="hm-skeleton" style={{ width: 180, height: 34, marginTop: 6 }} />
                  )}
                </div>
                <div
                  style={{
                    ...styles.changeBadge,
                    backgroundColor: up ? "rgba(16,185,129,0.14)" : "rgba(239,68,68,0.14)",
                    color: accent,
                    border: `1px solid ${accent}30`,
                  }}
                >
                  {up ? "▲" : "▼"} {up ? "+" : ""}{priceChange.toFixed(2)}%
                </div>
              </div>

              <div style={styles.chartWrapper}>
                <canvas ref={canvasRef} style={styles.canvas} />
              </div>

              <div style={styles.cardFooter}>
                <span style={styles.liveIndicator}>
                  <span
                    style={{
                      ...styles.liveDot,
                      backgroundColor: connected ? primaryColor : "#64748b",
                      boxShadow: connected ? `0 0 0 4px ${primarySoft}` : "none",
                      animation: connected ? "hm-pulse 1.6s ease-in-out infinite" : "none",
                    }}
                  />
                  {connected ? "Live Binance Feed" : "Connecting…"}
                </span>
                <span style={styles.timeframeText}>1m candles</span>
              </div>
            </div>
          </div>
        </section>
      </main>

      {showSecondHome && <SecondHome />}
      <ThirdHome />
      <FourthHome />
      <Footer />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */
const styles = {
  pageWrapper: {
    position: "relative",
    backgroundColor: "#070a12",
    color: "#f8fafc",
    minHeight: "100vh",
    overflowX: "hidden",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  bgLayer: { position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 },
  orb: { position: "absolute", borderRadius: "50%", filter: "blur(90px)" },
  orbA: { width: 620, height: 620, top: -180, right: -140, animation: "hm-float 14s ease-in-out infinite" },
  orbB: {
    width: 520, height: 520, bottom: -220, left: -160,
    background: "radial-gradient(circle, rgba(99,102,241,0.16) 0%, transparent 70%)",
    animation: "hm-float 18s ease-in-out infinite reverse",
  },
  grid: {
    position: "absolute", inset: 0,
    backgroundImage:
      "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
    backgroundSize: "64px 64px",
    maskImage: "radial-gradient(ellipse at 50% 0%, #000 40%, transparent 78%)",
  },
  container: {
    position: "relative", zIndex: 1,
    maxWidth: 1240, margin: "0 auto",
    padding: "clamp(32px,6vw,72px) clamp(20px,4vw,28px)",
    boxSizing: "border-box",
  },
  heroGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
    gap: "clamp(32px,5vw,64px)",
    alignItems: "center",
    minHeight: "72vh",
  },
  heroLeft: { display: "flex", flexDirection: "column", gap: 22, minWidth: 0 },
  badge: {
    display: "inline-flex", alignItems: "center", gap: 8,
    padding: "7px 14px", borderRadius: 999, border: "1px solid",
    fontSize: 13, fontWeight: 500, letterSpacing: "0.01em", width: "fit-content",
  },
  badgeDot: { width: 6, height: 6, borderRadius: "50%" },
  heading: {
    fontSize: "clamp(2.3rem, 4.6vw, 3.75rem)",
    fontWeight: 800, lineHeight: 1.08, letterSpacing: "-0.03em", margin: 0,
  },
  subheading: { fontSize: 17, lineHeight: 1.65, color: "#94a3b8", maxWidth: 540, margin: 0 },
  ctaGroup: { display: "flex", flexWrap: "wrap", gap: 14, marginTop: 6 },
  primaryBtn: {
    display: "inline-flex", alignItems: "center", gap: 9,
    padding: "15px 26px", borderRadius: 14, color: "#fff",
    fontWeight: 650, fontSize: 15, border: "none", cursor: "pointer",
  },
  secondaryBtn: {
    display: "inline-flex", alignItems: "center",
    padding: "15px 24px", borderRadius: 14,
    backgroundColor: "rgba(255,255,255,0.03)",
    fontWeight: 600, fontSize: 15, border: "1px solid", cursor: "pointer",
  },
  statStrip: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(96px, 1fr))",
    gap: 16, marginTop: 12, paddingTop: 24,
    borderTop: "1px solid rgba(255,255,255,0.07)",
  },
  stat: { minWidth: 0 },
  statK: { fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em", color: "#f1f5f9" },
  statV: { fontSize: 12, color: "#64748b", marginTop: 2 },
  featureGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: 14, marginTop: 8, marginRight: 20,
  },
  featureCard: {
    display: "flex", gap: 12, alignItems: "flex-start",
    padding: 14, borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.06)",
    backgroundColor: "rgba(255,255,255,0.02)",
  },
  iconBox: { padding: 9, borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  featureTitle: { margin: 0, fontSize: 14, fontWeight: 650, color: "#f1f5f9" },
  featureDesc: { margin: "4px 0 0", fontSize: 12.5, color: "#7c8899", lineHeight: 1.45 },
  heroRight: { width: "100%", minWidth: 0 },
  cardContainer: {
    backgroundColor: "rgba(13,18,32,0.72)",
    backdropFilter: "blur(20px)",
    borderRadius: 24,
    border: "1px solid rgba(255,255,255,0.09)",
    padding: "clamp(18px,3vw,26px)",
    boxShadow: "0 30px 70px -20px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,255,255,0.06)",
    marginBottom: 200,
    marginRight: 20,
  },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 18 },
  pairLabel: { fontSize: 12, fontWeight: 650, color: "#64748b", letterSpacing: "0.09em", textTransform: "uppercase" },
  priceDisplay: {
    fontSize: "clamp(26px,3.4vw,32px)", fontWeight: 750, color: "#f8fafc",
    marginTop: 6, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums",
  },
  changeBadge: { padding: "6px 11px", borderRadius: 10, fontSize: 13, fontWeight: 650, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  chartWrapper: { width: "100%", height: 250, position: "relative" },
  canvas: { width: "100%", height: "100%", display: "block" },
  cardFooter: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    marginTop: 18, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.06)",
  },
  liveIndicator: { display: "flex", alignItems: "center", gap: 9, fontSize: 12.5, color: "#94a3b8" },
  liveDot: { width: 7, height: 7, borderRadius: "50%" },
  timeframeText: { fontSize: 12.5, color: "#64748b" },
};
